import { el } from "./dom";

/**
 * Side picker — a two-option toggle for picking the player's color (white/black)
 * before a new game starts. Visual is a pair of large piece-like swatches: one
 * pale and one dark, the selected one highlighted with the brass accent ring.
 *
 * The component is rendering-agnostic: it accepts string values for the two
 * options so each game can label them with its own i18n strings (and even use
 * non-color labels if a future game has a different "first to move" concept).
 *
 * Controlled: pass the current `value` and the handler runs on toggle.
 */
export interface SidePickerOptions<T extends string = string> {
  readonly label: string;
  readonly leftValue: T;
  readonly leftLabel: string;
  readonly rightValue: T;
  readonly rightLabel: string;
  readonly value: T;
  readonly onChange: (next: T) => void;
}

export function createSidePicker<T extends string = string>(
  opts: SidePickerOptions<T>,
): HTMLElement {
  const makeSwatch = (
    side: "white" | "black",
    swatchValue: T,
    swatchLabel: string,
  ): HTMLButtonElement => {
    const selected = swatchValue === opts.value;
    const classes = ["side-picker__swatch", `side-picker__swatch--${side}`];
    if (selected) classes.push("side-picker__swatch--selected");
    return el(
      "button",
      {
        class: classes.join(" "),
        attrs: {
          type: "button",
          "aria-pressed": selected ? "true" : "false",
          // Sub-target for cross-repaint focus survival — see preserveFocus.ts.
          // The string form keeps the attribute selector simple.
          "data-side-value": String(swatchValue),
        },
        on: {
          click: () => {
            if (swatchValue !== opts.value) opts.onChange(swatchValue);
          },
        },
      },
      [
        el("span", { class: "side-picker__disc" }),
        el("span", { class: "side-picker__caption", text: swatchLabel }),
      ],
    );
  };

  return el(
    "div",
    {
      class: "side-picker",
      attrs: { "data-focus-key": `side:${opts.label}` },
    },
    [
      el("div", { class: "side-picker__label", text: opts.label }),
      el("div", { class: "side-picker__row" }, [
        makeSwatch("white", opts.leftValue, opts.leftLabel),
        makeSwatch("black", opts.rightValue, opts.rightLabel),
      ]),
    ],
  );
}
